import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, CheckCircle, IndianRupee, HardHat, ListChecks, FileText, Clock, BadgeCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PACKAGE_ICONS = {
  ai_feasibility: Sparkles,
  architect_basic: FileText,
  architect_standard: HardHat,
  architect_premium: HardHat,
  assisted_79a: ListChecks
};

export default function Services() {
  const { user } = useAuth();
  const isPrivileged = user?.is_master_admin || user?.is_sales_admin;
  const [searchParams, setSearchParams] = useSearchParams();
  const [society, setSociety] = useState(null);
  const [packages, setPackages] = useState([]);
  const [purchased, setPurchased] = useState({});
  const [loading, setLoading] = useState(true);
  const [payingFor, setPayingFor] = useState(null);
  const [paymentState, setPaymentState] = useState(null); // checking | paid | failed
  const pollCount = useRef(0);

  const fetchData = useCallback(async () => {
    try {
      const socRes = await fetch(`${API}/societies`, { credentials: "include" });
      const societies = await socRes.json();
      if (societies.length > 0) {
        const soc = societies[0];
        setSociety(soc);
        const pkgRes = await fetch(`${API}/payments/packages?society_id=${soc.society_id}`, { credentials: "include" });
        if (pkgRes.ok) {
          const data = await pkgRes.json();
          setPackages(data.packages);
          setPurchased(data.purchased);
        }
      }
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  }, []);

  const pollStatus = useCallback(async (sessionId) => {
    if (pollCount.current >= 6) {
      setPaymentState("failed");
      toast.error("Payment status check timed out. Contact support if amount was deducted.");
      return;
    }
    pollCount.current += 1;
    try {
      const res = await fetch(`${API}/payments/status/${sessionId}`, { credentials: "include" });
      const data = await res.json();
      if (data.payment_status === "paid") {
        setPaymentState("paid");
        toast.success("Payment successful! Service unlocked.");
        setSearchParams({});
        fetchData();
        return;
      }
      if (data.status === "expired") {
        setPaymentState("failed");
        toast.error("Payment session expired. Please try again.");
        return;
      }
      setTimeout(() => pollStatus(sessionId), 2000);
    } catch {
      setTimeout(() => pollStatus(sessionId), 2000);
    }
  }, [fetchData, setSearchParams]);

  useEffect(() => {
    fetchData();
    const sessionId = searchParams.get("session_id");
    if (sessionId) {
      setPaymentState("checking");
      pollCount.current = 0;
      pollStatus(sessionId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buy = async (packageId) => {
    if (!society) return;
    setPayingFor(packageId);
    try {
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          package_id: packageId,
          society_id: society.society_id,
          origin_url: window.location.origin
        }),
        credentials: "include"
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || "Failed to start payment");
        return;
      }
      window.location.href = data.url;
    } catch {
      toast.error("Failed to start payment");
      setPayingFor(null);
    }
  };

  const requestQuote = async (serviceName) => {
    const phone = window.prompt(`Leave your phone number and our team will reach out about "${serviceName}":`);
    if (phone === null) return;
    try {
      const res = await fetch(`${API}/leads/expert-review`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, note: `Interested in: ${serviceName}`, context: `Services page — ${serviceName}`, society_id: society?.society_id }),
        credentials: "include"
      });
      const d = await res.json();
      if (res.ok) toast.success(d.message || "Thanks — our team will reach out shortly.");
      else toast.error(d.detail || "Failed to submit request");
    } catch { toast.error("Failed to submit request"); }
  };

  const isPurchased = (pkg) => {
    if (pkg.package_id === "ai_feasibility") return purchased.ai_feasibility;
    if (pkg.package_id === "assisted_79a") return purchased.assisted_79a;
    if (pkg.package_id.startsWith("architect_")) return purchased.architect_tier === pkg.package_id.replace("architect_", "");
    return false;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96"><div className="spinner"></div></div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Services & Pricing</h1>
          <p className="text-slate-600">Pre-feasibility, self document upload & self 79A process are always free</p>
        </div>

        {isPrivileged && (
          <Card className="bg-purple-50 border-purple-200" data-testid="admin-demo-banner">
            <CardContent className="py-3 flex items-center gap-3">
              <BadgeCheck className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <p className="text-sm text-purple-800">
                <span className="font-semibold">{user?.is_master_admin ? "Master Admin" : "Sales Admin"} demo access:</span>{" "}
                all paid features (AI feasibility etc.) work for you without payment — ideal for client demos.
              </p>
            </CardContent>
          </Card>
        )}

        {paymentState === "checking" && (
          <Card className="bg-blue-50 border-blue-200" data-testid="payment-checking">
            <CardContent className="py-4 flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
              <p className="text-blue-800 font-medium">Verifying your payment...</p>
            </CardContent>
          </Card>
        )}
        {paymentState === "paid" && (
          <Card className="bg-emerald-50 border-emerald-200" data-testid="payment-success">
            <CardContent className="py-4 flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <p className="text-emerald-800 font-medium">Payment successful! Your service is now active.</p>
            </CardContent>
          </Card>
        )}

        {!society && (
          <Card className="border-emerald-200 bg-emerald-50/50" data-testid="services-needs-setup-note">
            <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="text-sm text-slate-700">Set up your society to see per-report pricing (AI feasibility, architect reports, assisted 79A).</p>
              <Button size="sm" onClick={() => window.location.href = '/setup'} className="bg-emerald-700 hover:bg-emerald-800 flex-shrink-0">Set up my society</Button>
            </CardContent>
          </Card>
        )}

        {packages.length > 0 && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-3" style={{ fontFamily: 'Manrope' }}>Pay per use</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {packages.map((pkg) => {
            const Icon = PACKAGE_ICONS[pkg.package_id] || Sparkles;
            const owned = isPurchased(pkg);
            const premium = pkg.package_id === "architect_premium";
            return (
              <Card
                key={pkg.package_id}
                className={`flex flex-col ${owned ? "border-emerald-300 bg-emerald-50/40" : premium ? "border-amber-300" : ""}`}
                data-testid={`service-card-${pkg.package_id}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${owned ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-600"}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    {owned && <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>}
                    {premium && !owned && <Badge className="bg-amber-100 text-amber-700">Most Comprehensive</Badge>}
                  </div>
                  <CardTitle className="text-base mt-2">{pkg.name}</CardTitle>
                  <CardDescription className="text-xs">{pkg.description}</CardDescription>
                </CardHeader>
                <CardContent className="mt-auto pt-2">
                  <div className="flex items-center gap-1 mb-3">
                    <IndianRupee className="w-5 h-5 text-slate-700" />
                    <span className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                      {pkg.amount.toLocaleString("en-IN")}
                    </span>
                    <span className="text-xs text-slate-500 ml-1">one-time</span>
                  </div>
                  <Button
                    className={`w-full ${owned ? "" : "bg-emerald-700 hover:bg-emerald-800"}`}
                    variant={owned ? "outline" : "default"}
                    disabled={owned || payingFor === pkg.package_id}
                    onClick={() => buy(pkg.package_id)}
                    data-testid={`buy-${pkg.package_id}-btn`}
                  >
                    {payingFor === pkg.package_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : owned ? (
                      <CheckCircle className="w-4 h-4 mr-2" />
                    ) : null}
                    {owned ? "Purchased" : "Pay & Unlock"}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
            </div>
          </div>
        )}

        {/* Managed services (upgrade path) */}
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-1" style={{ fontFamily: 'Manrope' }}>Managed services</h2>
          <p className="text-sm text-slate-600 mb-3">Hands-on help from the MangoHouse team — quoted per society.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { name: "Managed Redevelopment", desc: "End-to-end handholding from pre-feasibility to builder selection.", icon: HardHat },
              { name: "PMC / Builder Shortlisting", desc: "We shortlist and vet PMCs and builders for your society.", icon: ListChecks },
              { name: "Legal Document Drafting & Review", desc: "Draft/review conveyance, tender and development agreements.", icon: FileText }
            ].map((s) => {
              const I = s.icon;
              return (
                <Card key={s.name} className="flex flex-col" data-testid={`managed-service-${s.name}`}>
                  <CardHeader className="pb-2">
                    <div className="w-10 h-10 rounded-lg bg-slate-100 text-slate-600 flex items-center justify-center"><I className="w-5 h-5" /></div>
                    <CardTitle className="text-base mt-2">{s.name}</CardTitle>
                    <CardDescription className="text-xs">{s.desc}</CardDescription>
                  </CardHeader>
                  <CardContent className="mt-auto pt-2">
                    <p className="text-sm text-slate-500 mb-3">Custom pricing</p>
                    <Button variant="outline" className="w-full" onClick={() => requestQuote(s.name)} data-testid="get-quote-btn">Get a quote</Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        <Card className="bg-slate-50 border-slate-200">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Clock className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-slate-600">
                <p className="font-medium text-slate-800 mb-1">What's always free</p>
                <p>Quick & AI pre-feasibility checks • Uploading your own documents • Running the 79A process yourself • Member registration & voting • Zone/regulation lookup</p>
                <p className="mt-2 text-xs text-slate-500">These stay free because they're core to what ReDevelop exists for — becoming redevelopment ready.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
